import { useState, useEffect, useRef } from 'react'
import * as fabric from 'fabric'
import { useClientPreset } from '@/contexts/ClientPresetContext'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import {
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline'

interface TextLayer {
  id: string
  text: string
  fontFamily: string
  fontSize: number
  fontWeight: string
  fontStyle: string
  fill: string
  textDecoration: string
  textAlign: string
  backgroundColor: string
  stroke: string
  strokeWidth: number
  fabricObject?: fabric.IText
}

const CANVAS_RATIOS = [
  { name: 'Square (1:1)', ratio: 1, width: 1080, height: 1080 },
  { name: 'Story (9:16)', ratio: 0.5625, width: 1080, height: 1920 },
  { name: 'Portrait (4:5)', ratio: 0.8, width: 1080, height: 1350 },
  { name: 'Landscape (1.91:1)', ratio: 1.91, width: 1200, height: 628 },
]

const GOOGLE_FONTS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Impact',
  'Comic Sans MS',
  'Trebuchet MS',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Raleway',
  'Poppins',
  'Merriweather',
  'Playfair Display',
]

export default function AssetEditorPage() {
  const { selectedPreset } = useClientPreset()
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null)
  const [selectedRatio, setSelectedRatio] = useState(CANVAS_RATIOS[0])
  const [imageOpacity, setImageOpacity] = useState(1)
  const [baseImage, setBaseImage] = useState<fabric.Image | null>(null)
  const [textLayers, setTextLayers] = useState<TextLayer[]>([])
  const [selectedTextLayer, setSelectedTextLayer] = useState<string | null>(null)
  const [assetTitle, setAssetTitle] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [imageScale, setImageScale] = useState(1)
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [isCropMode, setIsCropMode] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialize canvas
  useEffect(() => {
    if (canvasRef.current && !canvas) {
      const fabricCanvas = new fabric.Canvas(canvasRef.current, {
        backgroundColor: '#ffffff',
        preserveObjectStacking: true,
      })
      
      // Set initial size with proper display scaling
      const maxDisplaySize = 600
      let displayWidth = selectedRatio.width
      let displayHeight = selectedRatio.height
      
      if (selectedRatio.width > maxDisplaySize || selectedRatio.height > maxDisplaySize) {
        const scale = Math.min(maxDisplaySize / selectedRatio.width, maxDisplaySize / selectedRatio.height)
        displayWidth = selectedRatio.width * scale
        displayHeight = selectedRatio.height * scale
      }

      fabricCanvas.setWidth(selectedRatio.width)
      fabricCanvas.setHeight(selectedRatio.height)
      fabricCanvas.setDimensions({
        width: displayWidth,
        height: displayHeight
      }, { cssOnly: true })
      
      setCanvas(fabricCanvas)

      // Handle object selection
      fabricCanvas.on('selection:created', (e: any) => {
        if (e.selected && e.selected[0] && e.selected[0].type === 'i-text') {
          const textObj = e.selected[0] as fabric.IText
          const layer = textLayers.find(l => l.fabricObject === textObj)
          if (layer) {
            setSelectedTextLayer(layer.id)
          }
        }
      })

      fabricCanvas.on('selection:updated', (e: any) => {
        if (e.selected && e.selected[0] && e.selected[0].type === 'i-text') {
          const textObj = e.selected[0] as fabric.IText
          const layer = textLayers.find(l => l.fabricObject === textObj)
          if (layer) {
            setSelectedTextLayer(layer.id)
          }
        }
      })

      fabricCanvas.on('selection:cleared', () => {
        setSelectedTextLayer(null)
      })

      return () => {
        fabricCanvas.dispose()
      }
    }
  }, [canvasRef.current])

  // Update canvas size when ratio changes
  useEffect(() => {
    if (canvas) {
      // Calculate display size (max 600px for better UI)
      const maxDisplaySize = 600
      let displayWidth = selectedRatio.width
      let displayHeight = selectedRatio.height
      
      if (selectedRatio.width > maxDisplaySize || selectedRatio.height > maxDisplaySize) {
        const scale = Math.min(maxDisplaySize / selectedRatio.width, maxDisplaySize / selectedRatio.height)
        displayWidth = selectedRatio.width * scale
        displayHeight = selectedRatio.height * scale
      }

      // Set canvas dimensions
      canvas.setWidth(selectedRatio.width)
      canvas.setHeight(selectedRatio.height)
      
      // Set display dimensions
      canvas.setDimensions({
        width: displayWidth,
        height: displayHeight
      }, { cssOnly: true })
      
      canvas.renderAll()
      
      // Resize base image if it exists
      if (baseImage) {
        const originalWidth = baseImage.width || 1
        const originalHeight = baseImage.height || 1
        const baseScale = Math.min(
          selectedRatio.width / originalWidth,
          selectedRatio.height / originalHeight
        )
        
        baseImage.set({
          scaleX: baseScale * imageScale,
          scaleY: baseScale * imageScale,
          left: selectedRatio.width / 2 + imagePosition.x,
          top: selectedRatio.height / 2 + imagePosition.y,
          originX: 'center',
          originY: 'center',
        })
        canvas.renderAll()
      }
    }
  }, [selectedRatio, canvas, baseImage, imageScale, imagePosition])

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !canvas) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const imgUrl = event.target?.result as string
      
      fabric.Image.fromURL(imgUrl).then((img: any) => {
        // Remove old base image if exists
        if (baseImage) {
          canvas.remove(baseImage)
        }

        // Scale image to fit canvas
        const scaleX = selectedRatio.width / (img.width || 1)
        const scaleY = selectedRatio.height / (img.height || 1)
        const scale = Math.min(scaleX, scaleY)

        img.set({
          scaleX: scale * imageScale,
          scaleY: scale * imageScale,
          left: selectedRatio.width / 2 + imagePosition.x,
          top: selectedRatio.height / 2 + imagePosition.y,
          originX: 'center',
          originY: 'center',
          opacity: imageOpacity,
          selectable: isCropMode,
          evented: isCropMode,
        })

        canvas.add(img)
        canvas.sendObjectToBack(img)
        setBaseImage(img)
        canvas.renderAll()
      })
    }
    reader.readAsDataURL(file)
  }

  // Update image properties
  useEffect(() => {
    if (baseImage && canvas) {
      // Get the original dimensions
      const originalWidth = baseImage.width || 1
      const originalHeight = baseImage.height || 1
      
      // Calculate the base scale to fit the canvas
      const baseScale = Math.min(
        selectedRatio.width / originalWidth,
        selectedRatio.height / originalHeight
      )
      
      // Apply all transformations
      const finalScaleX = baseScale * imageScale
      const finalScaleY = baseScale * imageScale
      
      // Calculate centered position with offset
      const centerX = selectedRatio.width / 2
      const centerY = selectedRatio.height / 2
      
      baseImage.set({
        opacity: imageOpacity,
        scaleX: finalScaleX,
        scaleY: finalScaleY,
        left: centerX + imagePosition.x,
        top: centerY + imagePosition.y,
        originX: 'center',
        originY: 'center',
        selectable: isCropMode,
        evented: isCropMode,
      })
      
      canvas.renderAll()
    }
  }, [imageOpacity, imageScale, imagePosition, isCropMode, baseImage, canvas, selectedRatio])

  // Toggle crop mode
  const toggleCropMode = () => {
    setIsCropMode(!isCropMode)
    if (baseImage && canvas) {
      baseImage.set({
        selectable: !isCropMode,
        evented: !isCropMode,
      })
      canvas.renderAll()
    }
  }

  // Reset image adjustments
  const resetImageAdjustments = () => {
    setImageScale(1)
    setImagePosition({ x: 0, y: 0 })
    setImageOpacity(1)
    setIsCropMode(false)
  }

  // Add text layer
  const addTextLayer = () => {
    if (!canvas) return

    const newLayer: TextLayer = {
      id: Date.now().toString(),
      text: 'Double click to edit',
      fontFamily: 'Arial',
      fontSize: 40,
      fontWeight: 'normal',
      fontStyle: 'normal',
      fill: '#000000',
      textDecoration: '',
      textAlign: 'left',
      backgroundColor: '',
      stroke: '',
      strokeWidth: 0,
    }

    const text = new fabric.IText(newLayer.text, {
      left: selectedRatio.width / 2,
      top: selectedRatio.height / 2,
      fontFamily: newLayer.fontFamily,
      fontSize: newLayer.fontSize,
      fontWeight: newLayer.fontWeight,
      fontStyle: newLayer.fontStyle,
      fill: newLayer.fill,
      textAlign: newLayer.textAlign as any,
      backgroundColor: newLayer.backgroundColor,
      stroke: newLayer.stroke,
      strokeWidth: newLayer.strokeWidth,
      originX: 'center',
      originY: 'center',
    })

    newLayer.fabricObject = text
    canvas.add(text)
    canvas.setActiveObject(text)
    canvas.renderAll()

    setTextLayers([...textLayers, newLayer])
    setSelectedTextLayer(newLayer.id)

    // Update layer when text changes
    text.on('changed', () => {
      setTextLayers(prev => prev.map(l => 
        l.id === newLayer.id ? { ...l, text: text.text || '' } : l
      ))
    })
  }

  // Update text layer
  const updateTextLayer = (id: string, updates: Partial<TextLayer>) => {
    const layer = textLayers.find(l => l.id === id)
    if (!layer || !layer.fabricObject || !canvas) return

    const textObj = layer.fabricObject

    if (updates.fontFamily !== undefined) textObj.set('fontFamily', updates.fontFamily)
    if (updates.fontSize !== undefined) textObj.set('fontSize', updates.fontSize)
    if (updates.fontWeight !== undefined) textObj.set('fontWeight', updates.fontWeight)
    if (updates.fontStyle !== undefined) textObj.set('fontStyle', updates.fontStyle)
    if (updates.fill !== undefined) textObj.set('fill', updates.fill)
    if (updates.textAlign !== undefined) textObj.set('textAlign', updates.textAlign as any)
    if (updates.backgroundColor !== undefined) textObj.set('backgroundColor', updates.backgroundColor)
    if (updates.stroke !== undefined) textObj.set('stroke', updates.stroke)
    if (updates.strokeWidth !== undefined) textObj.set('strokeWidth', updates.strokeWidth)
    if (updates.textDecoration !== undefined) {
      textObj.set('underline', updates.textDecoration === 'underline')
      textObj.set('linethrough', updates.textDecoration === 'line-through')
    }

    canvas.renderAll()
    setTextLayers(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l))
  }

  // Delete text layer
  const deleteTextLayer = (id: string) => {
    const layer = textLayers.find(l => l.id === id)
    if (!layer || !layer.fabricObject || !canvas) return

    canvas.remove(layer.fabricObject)
    canvas.renderAll()
    setTextLayers(prev => prev.filter(l => l.id !== id))
    if (selectedTextLayer === id) {
      setSelectedTextLayer(null)
    }
  }

  // Download as image
  const downloadImage = () => {
    if (!canvas) return

    canvas.discardActiveObject()
    canvas.renderAll()

    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 1,
    })

    const link = document.createElement('a')
    link.download = assetTitle ? `${assetTitle}.png` : `asset-${Date.now()}.png`
    link.href = dataURL
    link.click()
    toast.success('Image downloaded successfully!')
  }

  // Save to Meta
  const saveToMeta = async () => {
    if (!canvas || !selectedPreset) {
      toast.error('Please select a client first')
      return
    }

    if (!assetTitle.trim()) {
      toast.error('Please enter an asset title')
      return
    }

    setIsSaving(true)
    try {
      canvas.discardActiveObject()
      canvas.renderAll()

      // Convert canvas to blob
      const dataURL = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 1,
      })

      // Convert dataURL to blob
      const blob = await (await fetch(dataURL)).blob()
      
      // Extract client ID from preset ID (remove 'db-client-' prefix)
      const clientId = selectedPreset.id.replace('db-client-', '')
      
      // Create form data
      const formData = new FormData()
      formData.append('files', blob, `${assetTitle}.png`)
      formData.append('client_id', clientId)
      formData.append('titles', assetTitle)
      formData.append('descriptions', 'Created with Asset Editor')

      // Upload to Meta
      const response = await api.post('/assets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      toast.success('Asset saved to Meta successfully!')
      console.log('Upload response:', response.data)
      
      // Clear canvas after successful save
      if (confirm('Asset saved! Would you like to start a new design?')) {
        clearCanvas()
      }
    } catch (error: any) {
      console.error('Failed to save asset:', error)
      toast.error(error.response?.data?.detail || 'Failed to save asset to Meta')
    } finally {
      setIsSaving(false)
    }
  }

  // Clear canvas
  const clearCanvas = () => {
    if (!canvas) return
    
    canvas.clear()
    canvas.backgroundColor = '#ffffff'
    setBaseImage(null)
    setTextLayers([])
    setSelectedTextLayer(null)
    setAssetTitle('')
    setImageOpacity(1)
    setImageScale(1)
    setImagePosition({ x: 0, y: 0 })
    setIsCropMode(false)
    canvas.renderAll()
  }

  const selectedLayer = textLayers.find(l => l.id === selectedTextLayer)

  if (!selectedPreset) {
    return (
      <div className="p-8 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100" style={{ height: '100%' }}>
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-2xl p-8 shadow-xl max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="text-4xl">⚠️</span>
            <h2 className="text-xl font-bold text-yellow-900">No Client Selected</h2>
          </div>
          <p className="text-yellow-800 leading-relaxed mb-5">
            Please select a client before using the Asset Editor.
          </p>
          <a
            href="/presets"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Go to Clients →
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="asset-editor-page flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100" style={{ height: '100%' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Asset Editor
            </h1>
            <p className="text-sm text-gray-600 mt-1.5 font-medium">Create stunning ad assets with images and text</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="✨ Enter asset title..."
              value={assetTitle}
              onChange={(e) => setAssetTitle(e.target.value)}
              className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/70 backdrop-blur-sm placeholder:text-gray-400 hover:bg-white"
            />
            <button
              onClick={downloadImage}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl hover:from-gray-800 hover:to-gray-900 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span className="font-medium">Download</span>
            </button>
            <button
              onClick={saveToMeta}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <CloudArrowUpIcon className="w-5 h-5" />
              <span className="font-medium">{isSaving ? 'Saving...' : 'Save to Meta'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Tools */}
        <div className="w-80 bg-white/60 backdrop-blur-xl border-r border-gray-200/50 overflow-y-auto shadow-xl">
          <div className="p-6 space-y-6">
            {/* Canvas Ratio */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-200/50">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-xl">📐</span>
                Canvas Ratio
              </label>
              <select
                value={CANVAS_RATIOS.indexOf(selectedRatio)}
                onChange={(e) => setSelectedRatio(CANVAS_RATIOS[parseInt(e.target.value)])}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md cursor-pointer font-medium text-gray-700"
              >
                {CANVAS_RATIOS.map((ratio, idx) => (
                  <option key={idx} value={idx}>
                    {ratio.name} ({ratio.width}x{ratio.height})
                  </option>
                ))}
              </select>
            </div>

            {/* Image Upload */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-200/50">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-xl">🖼️</span>
                Base Image
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-3 px-4 py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group bg-white shadow-sm hover:shadow-md"
              >
                <PhotoIcon className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors">Choose Image</span>
              </button>
            </div>

            {/* Image Controls */}
            {true && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-xl">🎛️</span>
                  Image Controls
                </label>
                
                {/* Crop Mode Toggle */}
                <div className="mb-4">
                  <button
                    onClick={toggleCropMode}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                      isCropMode
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">{isCropMode ? '✂️' : '🔒'}</span>
                    {isCropMode ? 'Crop Mode ON' : 'Enable Crop Mode'}
                  </button>
                  {isCropMode && (
                    <p className="text-xs text-gray-600 mt-2 text-center">
                      Drag and resize the image on canvas
                    </p>
                  )}
                </div>

                {/* Image Scale */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Scale: <span className="text-blue-600">{Math.round(imageScale * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={imageScale}
                    onChange={(e) => setImageScale(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${((imageScale - 0.1) / (3 - 0.1)) * 100}%, rgb(229, 231, 235) ${((imageScale - 0.1) / (3 - 0.1)) * 100}%, rgb(229, 231, 235) 100%)`
                    }}
                  />
                </div>

                {/* Image Position */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Position</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">X: {imagePosition.x}px</label>
                      <input
                        type="range"
                        min="-200"
                        max="200"
                        value={imagePosition.x}
                        onChange={(e) => setImagePosition(prev => ({ ...prev, x: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Y: {imagePosition.y}px</label>
                      <input
                        type="range"
                        min="-200"
                        max="200"
                        value={imagePosition.y}
                        onChange={(e) => setImagePosition(prev => ({ ...prev, y: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                {/* Image Opacity */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Opacity: <span className="text-blue-600">{Math.round(imageOpacity * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={imageOpacity}
                    onChange={(e) => setImageOpacity(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${imageOpacity * 100}%, rgb(229, 231, 235) ${imageOpacity * 100}%, rgb(229, 231, 235) 100%)`
                    }}
                  />
                </div>

                {/* Reset Button */}
                <button
                  onClick={resetImageAdjustments}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium text-sm"
                >
                  <span className="text-lg">🔄</span>
                  Reset Image
                </button>
              </div>
            )}

            {/* Add Text Button */}
            <div>
              <button
                onClick={addTextLayer}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
              >
                <PlusIcon className="w-6 h-6" />
                <span>Add Text Layer</span>
              </button>
            </div>

            {/* Text Layers List */}
            {textLayers.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="text-xl">📝</span>
                  Text Layers
                  <span className="ml-auto text-xs bg-gradient-to-r from-blue-600 to-purple-600 text-white px-2.5 py-1 rounded-full font-bold">
                    {textLayers.length}
                  </span>
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {textLayers.map((layer) => (
                    <div
                      key={layer.id}
                      className={`p-3 border rounded-xl cursor-pointer transition-all duration-200 group ${
                        selectedTextLayer === layer.id
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-md'
                          : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-sm'
                      }`}
                      onClick={() => {
                        setSelectedTextLayer(layer.id)
                        if (layer.fabricObject && canvas) {
                          canvas.setActiveObject(layer.fabricObject)
                          canvas.renderAll()
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate flex-1 text-gray-700 group-hover:text-gray-900">
                          {layer.text || 'Empty text'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteTextLayer(layer.id)
                          }}
                          className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center p-8 overflow-auto relative">
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, gray 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
          
          <div className="bg-white shadow-2xl rounded-2xl overflow-hidden relative z-10 ring-1 ring-gray-200/50" style={{ 
            maxWidth: '100%',
            maxHeight: '100%',
          }}>
            <canvas ref={canvasRef} />
          </div>
        </div>

        {/* Right Sidebar - Text Properties */}
        {selectedLayer && (
          <div className="w-96 bg-white/60 backdrop-blur-xl border-l border-gray-200/50 shadow-xl flex flex-col">
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div className="flex items-center gap-2 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-2xl shadow-lg">
                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                <h3 className="text-base font-bold">Text Properties</h3>
              </div>

              {/* Font Family */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  Font Family
                </label>
                <select
                  value={selectedLayer.fontFamily}
                  onChange={(e) => updateTextLayer(selectedLayer.id, { fontFamily: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md cursor-pointer font-medium"
                  style={{ fontFamily: selectedLayer.fontFamily }}
                >
                  {GOOGLE_FONTS.map((font) => (
                    <option key={font} value={font} style={{ fontFamily: font }}>
                      {font}
                    </option>
                  ))}
                </select>
              </div>

              {/* Font Size */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Font Size: <span className="text-blue-600">{selectedLayer.fontSize}px</span>
                </label>
                <input
                  type="range"
                  min="12"
                  max="200"
                  value={selectedLayer.fontSize}
                  onChange={(e) => updateTextLayer(selectedLayer.id, { fontSize: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${((selectedLayer.fontSize - 12) / (200 - 12)) * 100}%, rgb(229, 231, 235) ${((selectedLayer.fontSize - 12) / (200 - 12)) * 100}%, rgb(229, 231, 235) 100%)`
                  }}
                />
              </div>

              {/* Text Color */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">🎨</span>
                  Text Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedLayer.fill}
                    onChange={(e) => updateTextLayer(selectedLayer.id, { fill: e.target.value })}
                    className="w-14 h-12 border-2 border-gray-300 rounded-xl cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                  />
                  <input
                    type="text"
                    value={selectedLayer.fill}
                    onChange={(e) => updateTextLayer(selectedLayer.id, { fill: e.target.value })}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm font-mono text-sm"
                  />
                </div>
              </div>

              {/* Font Style Buttons */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Style
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateTextLayer(selectedLayer.id, {
                      fontWeight: selectedLayer.fontWeight === 'bold' ? 'normal' : 'bold'
                    })}
                    className={`flex-1 px-4 py-3 border-2 rounded-xl font-bold text-lg transition-all duration-200 ${
                      selectedLayer.fontWeight === 'bold'
                        ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white border-blue-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    B
                  </button>
                  <button
                    onClick={() => updateTextLayer(selectedLayer.id, {
                      fontStyle: selectedLayer.fontStyle === 'italic' ? 'normal' : 'italic'
                    })}
                    className={`flex-1 px-4 py-3 border-2 rounded-xl italic text-lg transition-all duration-200 ${
                      selectedLayer.fontStyle === 'italic'
                        ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white border-blue-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    I
                  </button>
                  <button
                    onClick={() => updateTextLayer(selectedLayer.id, {
                      textDecoration: selectedLayer.textDecoration === 'underline' ? '' : 'underline'
                    })}
                    className={`flex-1 px-4 py-3 border-2 rounded-xl underline text-lg transition-all duration-200 ${
                      selectedLayer.textDecoration === 'underline'
                        ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white border-blue-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    U
                  </button>
                </div>
              </div>

              {/* Text Align */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Alignment
                </label>
                <div className="flex gap-2">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      onClick={() => updateTextLayer(selectedLayer.id, { textAlign: align })}
                      className={`flex-1 px-4 py-3 border-2 rounded-xl transition-all duration-200 font-medium ${
                        selectedLayer.textAlign === align
                          ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white border-blue-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300 hover:shadow-sm'
                      }`}
                    >
                      {align.charAt(0).toUpperCase() + align.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Background Color */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">🎭</span>
                  Background Color
                  <span className="ml-auto text-xs text-gray-500 font-normal">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedLayer.backgroundColor || '#ffffff'}
                    onChange={(e) => updateTextLayer(selectedLayer.id, { backgroundColor: e.target.value })}
                    className="w-14 h-12 border-2 border-gray-300 rounded-xl cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                  />
                  <input
                    type="text"
                    value={selectedLayer.backgroundColor}
                    onChange={(e) => updateTextLayer(selectedLayer.id, { backgroundColor: e.target.value })}
                    placeholder="transparent"
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm font-mono text-sm"
                  />
                </div>
              </div>

              {/* Text Stroke/Outline */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="text-lg">⭕</span>
                  Text Outline
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedLayer.stroke || '#000000'}
                      onChange={(e) => updateTextLayer(selectedLayer.id, { stroke: e.target.value })}
                      className="w-14 h-12 border-2 border-gray-300 rounded-xl cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                    />
                    <input
                      type="text"
                      value={selectedLayer.stroke}
                      onChange={(e) => updateTextLayer(selectedLayer.id, { stroke: e.target.value })}
                      placeholder="none"
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Outline Width: <span className="text-blue-600">{selectedLayer.strokeWidth}px</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={selectedLayer.strokeWidth}
                      onChange={(e) => updateTextLayer(selectedLayer.id, { strokeWidth: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${(selectedLayer.strokeWidth / 20) * 100}%, rgb(229, 231, 235) ${(selectedLayer.strokeWidth / 20) * 100}%, rgb(229, 231, 235) 100%)`
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Delete Text Button */}
              <button
                onClick={() => deleteTextLayer(selectedLayer.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold mt-2"
              >
                <TrashIcon className="w-5 h-5" />
                Delete Text Layer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

